'use client';

import classNames from 'classnames';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { AboveFoldProvider } from '@/libraries/contexts/above-fold-context';
import useAboveFold from '@/libraries/hooks/use-above-fold';
import useAnchorMenu from '@/libraries/hooks/use-anchor-menu';
import useIsInView from '@/libraries/hooks/use-is-in-view';
import { useScrollTheme } from '@/libraries/hooks/use-scroll-theme';
import styles from './module-wrapper.module.scss';

export interface ModuleWrapperProps {
  allowOverflow?: boolean;
  children?: React.ReactNode;
  className?: string;
  hasInPageNavigation?: boolean;
  hasSearchBar?: boolean;
  id?: string;
  isFirst?: boolean;
  padding?: 'headerOffset' | 'none' | 'default';
  setFooterBackground?: boolean;
  settings?: Content.Components['DefaultModuleSettingsDto'];
  style?: React.CSSProperties;
}

const ModuleWrapper = forwardRef<HTMLDivElement, ModuleWrapperProps>(
  (
    {
      allowOverflow = false,
      children,
      className,
      hasInPageNavigation = false,
      hasSearchBar = false,
      id,
      isFirst = false,
      padding = 'default',
      setFooterBackground = false,
      settings,
      style,
    },
    ref,
  ) => {
    const componentRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => componentRef.current as HTMLDivElement, [
      componentRef,
    ]);

    const { isInView } = useIsInView(componentRef, {
      rootMargin: '-20% 0px -80%',
    });
    const isAboveFold = useAboveFold(componentRef);
    const { setActiveAnchor } = useAnchorMenu();

    useScrollTheme(componentRef, settings?.theme);

    useEffect(() => {
      if (!settings?.anchorLinkSettings?.anchorId) return;

      if (!componentRef.current) return;

      if (!isInView) return;

      setActiveAnchor(settings.anchorLinkSettings.anchorId);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [componentRef, isInView, settings?.anchorLinkSettings?.anchorId]);

    useEffect(() => {
      if (!setFooterBackground) return;

      document.documentElement.dataset.footerBackground =
        settings?.theme ?? 'light';
    }, [setFooterBackground, settings?.theme]);

    const moduleId = useMemo(() => {
      if (!settings?.anchorLinkSettings?.anchorId) return id;

      return settings.anchorLinkSettings.anchorId;
    }, [settings?.anchorLinkSettings, id]);

    return (
      <div
        ref={componentRef}
        className={classNames(styles.component, className, {
          [styles.hasBottomIllustrations]: settings?.showBottomIllustrations,
          [styles.hasDefaultPadding]: padding === 'default',
          [styles.hasHeaderOffset]: padding === 'headerOffset',
          [styles.hasInPageNavigation]: hasInPageNavigation,
          [styles.hasOverflow]: allowOverflow,
          [styles.hasSearchBar]: hasSearchBar,
          [styles.hasTopIllustrations]: settings?.showTopIllustrations,
          [styles.hasTransparentBackground]:
            settings?.showTransparentBackground,
          [styles.isFirst]: isFirst,
        })}
        data-theme={settings?.theme ?? 'light'}
        id={moduleId}
        style={style}
      >
        <AboveFoldProvider value={isAboveFold}>{children}</AboveFoldProvider>
      </div>
    );
  },
);

ModuleWrapper.displayName = 'ModuleWrapper';

export default ModuleWrapper;
